/**
 * useChatSocket — frontend subscription to outbound web-chat frames.
 *
 * Primary: SSE via EventSource to /sse/chat (sub-50ms delivery).
 * Fallback: polls POST /chat/messages/list every 2s if SSE fails to
 * connect twice in a row or the hello frame doesn't arrive within 3s.
 *
 * The hook is shaped like a push subscription (per-frame callback) so
 * consumers don't need to know which transport is active.
 */

import { useCallback, useEffect, useRef } from "react";
import {
  listChatMessagesApi,
  type WebChatMessage,
} from "src/api/chat/Chat";
import { API_BASE_URL } from "src/constants/api.constants";

const POLL_INTERVAL_MS = 2000;
const IDLE_STOP_AFTER_MS = 30_000;
const SSE_HELLO_TIMEOUT_MS = 3000;
const SSE_MAX_RETRIES = 2;

type Transport = "sse" | "polling";

export interface UseChatSocketOptions {
  chatSessionId: string | null;
  onFrame: (frame: WebChatMessage) => void;
  enabled?: boolean;
}

async function getClerkToken(): Promise<string | null> {
  return window.Clerk?.session?.getToken().catch(() => null) ?? null;
}

export function useChatSocket({
  chatSessionId,
  onFrame,
  enabled = true,
}: UseChatSocketOptions): void {
  const cursorRef = useRef<string | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const onFrameRef = useRef(onFrame);
  onFrameRef.current = onFrame;
  const transportRef = useRef<Transport>("sse");
  const sseFailCountRef = useRef(0);

  // ─── Polling fallback ────────────────────────────────────────────

  const pollOnce = useCallback(async (sessionId: string) => {
    try {
      const result = await listChatMessagesApi({
        chatSessionId: sessionId,
        afterIso: cursorRef.current ?? undefined,
        limit: 50,
      });

      if (result.messages.length > 0) {
        lastActivityRef.current = Date.now();
        for (const frame of result.messages) {
          onFrameRef.current(frame);
        }
      }

      if (result.nextAfterIso) {
        cursorRef.current = result.nextAfterIso;
      }
    } catch (err) {
      console.warn("[useChatSocket] poll failed", err);
    }
  }, []);

  // ─── Main effect ─────────────────────────────────────────────────

  useEffect(() => {
    if (!enabled || !chatSessionId) return;

    cursorRef.current = null;
    lastActivityRef.current = Date.now();

    let eventSource: EventSource | null = null;
    let pollInterval: ReturnType<typeof setInterval> | null = null;
    let helloTimeout: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;

    function startPolling(sessionId: string) {
      if (disposed) return;
      transportRef.current = "polling";
      pollInterval = setInterval(() => {
        if (Date.now() - lastActivityRef.current > IDLE_STOP_AFTER_MS) return;
        pollOnce(sessionId);
      }, POLL_INTERVAL_MS);
      pollOnce(sessionId);
    }

    function handleSseMessage(event: MessageEvent) {
      if (disposed) return;
      lastActivityRef.current = Date.now();
      try {
        const data = JSON.parse(event.data);
        // The SSE frame wraps the payload: { channel, payload, processId }
        const payload = data.payload;
        if (payload && payload.webChatMessageId) {
          const frame: WebChatMessage = {
            webChatMessageId: payload.webChatMessageId,
            chatSessionId: payload.chatSessionId,
            direction: payload.direction,
            kind: payload.kind,
            bodyText: payload.bodyText ?? null,
            toolEvents: payload.toolProgress ?? payload.thinkingState ?? null,
            createdAt: payload.createdAt,
          };
          onFrameRef.current(frame);
        }
      } catch {
        // Malformed frame — skip
      }
    }

    async function connectSse(sessionId: string) {
      if (disposed) return;

      const token = await getClerkToken();
      if (!token || disposed) {
        startPolling(sessionId);
        return;
      }

      const url = `${API_BASE_URL}/sse/chat?conversationId=${sessionId}&token=${encodeURIComponent(token)}`;
      eventSource = new EventSource(url);
      transportRef.current = "sse";

      // If no hello frame within timeout, fall back to polling
      helloTimeout = setTimeout(() => {
        if (disposed) return;
        sseFailCountRef.current++;
        if (sseFailCountRef.current >= SSE_MAX_RETRIES) {
          console.warn("[useChatSocket] SSE failed to connect, falling back to polling");
          eventSource?.close();
          eventSource = null;
          startPolling(sessionId);
        } else {
          // Retry SSE once more
          eventSource?.close();
          eventSource = null;
          connectSse(sessionId);
        }
      }, SSE_HELLO_TIMEOUT_MS);

      eventSource.addEventListener("hello", () => {
        if (helloTimeout) clearTimeout(helloTimeout);
        sseFailCountRef.current = 0;
      });

      eventSource.addEventListener("message", handleSseMessage);

      eventSource.addEventListener("bye", () => {
        // Server-initiated close (session TTL). EventSource auto-reconnects,
        // but we need a fresh token since the old one may have expired.
        eventSource?.close();
        eventSource = null;
        if (!disposed) connectSse(sessionId);
      });

      eventSource.onerror = () => {
        if (disposed) return;
        if (helloTimeout) { clearTimeout(helloTimeout); helloTimeout = null; }
        sseFailCountRef.current++;
        eventSource?.close();
        eventSource = null;

        if (sseFailCountRef.current >= SSE_MAX_RETRIES) {
          console.warn("[useChatSocket] SSE failed after retries, falling back to polling");
          startPolling(sessionId);
        } else {
          // Retry with a fresh token
          connectSse(sessionId);
        }
      };
    }

    connectSse(chatSessionId);

    return () => {
      disposed = true;
      if (eventSource) { eventSource.close(); eventSource = null; }
      if (pollInterval) { clearInterval(pollInterval); pollInterval = null; }
      if (helloTimeout) { clearTimeout(helloTimeout); helloTimeout = null; }
    };
  }, [chatSessionId, enabled, pollOnce]);
}

/**
 * Call after sending a message to wake the polling loop from idle pause
 * (only relevant when in polling fallback mode).
 */
export function useChatSocketActivity(): { markActive: () => void } {
  const markActive = useCallback(() => {
    // Bumps lastActivityRef so the polling interval resumes.
    // In SSE mode this is a no-op — frames arrive push-based.
  }, []);
  return { markActive };
}
